import { usePage } from '@inertiajs/react';
import { mutate } from 'swr';
import { Github } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { useEventSlug } from '@/hooks/use-event-slug';
import type { StateResponse } from '@/types/market';
import {
  formatBananas,
  formatPercent,
  formatPrice,
  formatSigned,
} from './format';
import { githubSignInPath } from './github';
import { stateKey, useLivePortfolio, useMarketState } from './live';
import { PriceChart } from './price-chart';
import { RollingNumber } from './rolling-number';
import { TradePanel } from './trade-panel';

export function ProjectDetailClient({
  projectId,
  initialState,
}: {
  projectId: string;
  initialState: StateResponse;
}) {
  const eventSlug = useEventSlug();
  const pathname = usePage().url.split('?')[0];
  const s = useMarketState(initialState);
  const { portfolio, refresh: refreshPortfolio } = useLivePortfolio(
    s,
    initialState.canTrade ?? initialState.myUserType === 'hacker',
  );

  const project = s.projects.find((p) => p.id === projectId);
  if (!project) return <p>Project not found.</p>;

  const resolved = s.market.status === 'resolved';
  const isWinner = resolved && s.market.winnerProjectId === projectId;

  const myPosition =
    portfolio && 'positions' in portfolio
      ? portfolio.positions.find((p) => p.projectId === projectId)
      : undefined;
  const myShares = myPosition?.shares ?? 0;
  const canTrade =
    (s.canTrade ?? s.myUserType === 'hacker') && s.market.status === 'open';
  // The prices arrive with the trade's broadcast; only the trader's own balance and
  // holdings need reading back.
  const onTraded = async () => {
    await Promise.all([mutate(stateKey(eventSlug)), refreshPortfolio()]);
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {project.logoUrl ? (
              <img
                src={project.logoUrl}
                alt={project.name}
                className="size-20 border-2 border-primary object-cover"
              />
            ) : (
              <div className="size-20 border-2 border-primary bg-muted" />
            )}
            <div>
              <p className="mb-1 font-mono text-primary/70 text-xs uppercase tracking-wider">
                Proyecto
              </p>
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-3xl text-primary sm:text-4xl">
                  {project.name}
                </h1>
                {isWinner && (
                  <span className="bg-primary px-2 py-1 font-mono text-[10px] text-background uppercase tracking-wider">
                    Ganador
                  </span>
                )}
              </div>
              <p className="mt-1 font-mono text-primary/70 text-sm">
                {project.oneliner}
              </p>
            </div>
          </div>
          <div className="border-2 border-primary bg-background p-4 text-center sm:min-w-[140px]">
            <div className="font-bold font-mono text-3xl text-primary sm:text-4xl">
              <RollingNumber value={project.price} format={formatPercent} />
            </div>
            <div className="mt-1 font-mono text-[10px] text-primary/70 uppercase tracking-wider">
              {resolved ? 'Probabilidad final' : 'Chance to win'}
            </div>
          </div>
        </div>
      </section>

      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <h2 className="mb-4 border-primary/20 border-b pb-3 font-bold text-primary text-xl">
          Evolución del precio
        </h2>
        <PriceChart
          projectId={projectId}
          livePrice={project.price}
          open={s.market.status === 'open'}
        />
      </section>

      {myPosition && myPosition.shares > 0 && (
        <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
          <h2 className="mb-4 font-bold text-primary text-xl">Tu posición</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="border-primary border-l-4 pl-4">
              <p className="font-mono text-primary/70 text-xs uppercase">
                Acciones
              </p>
              <p className="font-bold font-mono text-2xl text-primary">
                {myPosition.shares}
              </p>
            </div>
            <div className="border-primary border-l-4 pl-4">
              <p className="font-mono text-primary/70 text-xs uppercase">
                Costo promedio
              </p>
              <p className="font-bold font-mono text-2xl text-primary">
                {formatPrice(myPosition.avgCostBananas)}
              </p>
            </div>
            <div className="border-primary border-l-4 pl-4">
              <p className="font-mono text-primary/70 text-xs uppercase">
                Valor actual
              </p>
              <p className="font-bold font-mono text-2xl text-primary">
                <RollingNumber
                  value={myPosition.valueBananas}
                  format={formatBananas}
                />
              </p>
              <p
                className={`mt-1 font-mono text-xs ${
                  myPosition.pnlBananas >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                <RollingNumber
                  value={myPosition.pnlBananas}
                  format={(n) => formatSigned(n, ' 🍌')}
                />{' '}
                (
                <RollingNumber
                  value={myPosition.pnlPct}
                  format={(n) => `${n.toFixed(1)}%`}
                />
                )
              </p>
            </div>
          </div>
        </section>
      )}

      {canTrade ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TradePanel
            side="buy"
            project={project}
            state={s}
            myShares={myShares}
            onTraded={onTraded}
          />
          <TradePanel
            side="sell"
            project={project}
            state={s}
            myShares={myShares}
            onTraded={onTraded}
          />
        </div>
      ) : s.market.status !== 'open' ? (
        <section className="border-2 border-primary bg-background/80 p-6 text-center backdrop-blur-sm">
          <p className="font-mono text-primary/70 text-sm uppercase tracking-wider">
            El mercado no está abierto
          </p>
        </section>
      ) : s.myUserType === null ? (
        <section className="flex flex-col items-center gap-4 border-2 border-primary bg-background/80 p-6 text-center backdrop-blur-sm">
          <p className="font-mono text-primary/70 text-sm uppercase tracking-wider">
            Inicia sesión para operar
          </p>
          <Button
            onClick={() => {
              window.location.href = githubSignInPath(pathname);
            }}
            className="rounded-none bg-primary py-6 font-bold text-background uppercase tracking-wider hover:bg-primary/90"
          >
            <Github className="mr-2 size-5" />
            Iniciar sesión con GitHub
          </Button>
        </section>
      ) : (
        <section className="border-2 border-primary bg-background/80 p-6 text-center backdrop-blur-sm">
          <p className="font-mono text-primary/70 text-sm uppercase tracking-wider">
            Solo los hackers pueden operar
          </p>
        </section>
      )}
    </div>
  );
}
